export type LegalMarkdownBlock =
  | { type: "heading"; level: number; text: string }
  | { type: "revision"; text: string }
  | { type: "paragraph"; text: string }
  | { type: "list"; items: string[] }
  | { type: "table"; headers: string[]; rows: string[][] };

export interface ParsedLegalMarkdown {
  title: string;
  revisionNote: string;
  blocks: LegalMarkdownBlock[];
}

const stripInlineMarkdown = (value: string): string => value
  .replace(/\[([^\]]+)]\(([^)]+)\)/g, "$1")
  .replace(/\*\*\*([^*]+)\*\*\*/g, "$1")
  .replace(/\*\*([^*]+)\*\*/g, "$1")
  .replace(/^_([^_]+)_$/, "$1")
  .replace(/<br\s*\/?>/gi, "\n")
  .replace(/\\([\\|])/g, "$1")
  .trim();

const splitTableRow = (line: string): string[] => line
  .trim()
  .replace(/^\|/, "")
  .replace(/\|$/, "")
  .split(/(?<!\\)\|/)
  .map(stripInlineMarkdown);

const isTableSeparator = (line: string): boolean => {
  const cells = splitTableRow(line);
  return cells.length > 0 && cells.every((cell) => /^:?-{3,}:?$/.test(cell));
};

export const parseLegalMarkdown = (markdown: string): ParsedLegalMarkdown => {
  const lines = markdown
    .replace(/<!--[^]*?-->/g, "")
    .replace(/\r\n?/g, "\n")
    .split("\n");

  const blocks: LegalMarkdownBlock[] = [];
  for (let index = 0; index < lines.length;) {
    const line = lines[index].trim();
    if (!line) {
      index += 1;
      continue;
    }

    if (line.startsWith("|") && index + 1 < lines.length && isTableSeparator(lines[index + 1])) {
      const headers = splitTableRow(line);
      const rows: string[][] = [];
      index += 2;
      while (index < lines.length && lines[index].trim().startsWith("|")) {
        rows.push(splitTableRow(lines[index]));
        index += 1;
      }
      blocks.push({ type: "table", headers, rows });
      continue;
    }

    const heading = line.match(/^(#{1,6})\s+(.+)$/);
    if (heading) {
      blocks.push({ type: "heading", level: heading[1].length, text: stripInlineMarkdown(heading[2]) });
      index += 1;
      continue;
    }

    if (/^_[^_]+_$/.test(line)) {
      blocks.push({ type: "revision", text: stripInlineMarkdown(line) });
      index += 1;
      continue;
    }

    if (/^-\s+/.test(line)) {
      const items: string[] = [];
      while (index < lines.length && /^-\s+/.test(lines[index].trim())) {
        items.push(stripInlineMarkdown(lines[index].trim().replace(/^-\s+/, "")));
        index += 1;
        while (index < lines.length && !lines[index].trim()) index += 1;
      }
      blocks.push({ type: "list", items });
      continue;
    }

    const paragraph: string[] = [line];
    index += 1;
    while (index < lines.length && lines[index].trim()) {
      const next = lines[index].trim();
      if (/^(#{1,6})\s+/.test(next) || next.startsWith("|") || /^-\s+/.test(next)) break;
      paragraph.push(next);
      index += 1;
    }
    blocks.push({ type: "paragraph", text: stripInlineMarkdown(paragraph.join(" ")) });
  }

  const title =
    blocks.find(
      (block): block is Extract<LegalMarkdownBlock, { type: "heading" }> =>
        block.type === "heading" && block.level === 1,
    )?.text ?? "";
  const revisionNote = blocks.find((block) => block.type === "revision")?.text ?? "";
  return { title, revisionNote, blocks };
};
